import * as stylex from "@stylexjs/stylex";
import { headerStyles as styles } from "./one-pager-header.styles";
import { useLayoutEffect, useRef, type ReactNode } from "react";
import { OnePagerLogo } from "./one-pager-logo";
import {
  OnePagerLinks,
  type OnePagerLinkRenderer,
  type PlayOnePagerLink,
} from "./one-pager-links";

export type PlayOnePagerCover = {
  readonly src: string;
  readonly focal: string;
  readonly focalPhone: string;
  readonly sources: readonly {
    readonly media?: string;
    readonly type: string;
    readonly srcSet: string;
    readonly sizes: string;
  }[];
};

const renderNativeLink: OnePagerLinkRenderer = (props) => (
  <a {...props}>{props.children}</a>
);

export function PlayOnePagerHeader({
  accent,
  name,
  leagueNote,
  pitch,
  logo,
  monogram,
  cover,
  site,
  links,
  notice,
  live,
  renderLink = renderNativeLink,
}: {
  readonly accent: string;
  readonly name: string;
  readonly leagueNote: string | null;
  readonly pitch: string;
  readonly logo: string | null;
  readonly monogram: string;
  readonly cover: PlayOnePagerCover | null;
  readonly site: { readonly label: string; readonly href: string } | null;
  readonly links: readonly PlayOnePagerLink[];
  readonly notice: ReactNode;
  readonly live: ReactNode;
  readonly renderLink?: OnePagerLinkRenderer;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const text = textRef.current;

    if (row === null || text === null) {
      throw new Error(
        "The one-pager header must be mounted before measurement.",
      );
    }

    // OnePagerLogo caps its measured square so it cannot repeatedly rewrap this text.
    const publish = () =>
      row.style.setProperty(
        "--hero-text-height",
        text.getBoundingClientRect().height + "px",
      );
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(text);
    return () => observer.disconnect();
  }, []);

  return (
    <header {...stylex.props(styles.hero, styles.accent(accent))}>
      <div {...stylex.props(styles.coverBlank)} aria-hidden="true" />
      {cover ? (
        <picture>
          {cover.sources.map((source) => (
            <source key={source.type + (source.media ?? "")} {...source} />
          ))}
          <img
            {...stylex.props(
              styles.cover,
              styles.coverPosition(cover.focal, cover.focalPhone),
            )}
            src={cover.src}
            alt=""
            aria-hidden="true"
            decoding="async"
          />
        </picture>
      ) : null}
      <div {...stylex.props(styles.heroBody)}>
        <div ref={rowRef} {...stylex.props(styles.heroRow)}>
          <OnePagerLogo logo={logo} monogram={monogram} />
          <div ref={textRef} {...stylex.props(styles.heroText)}>
            <h1 {...stylex.props(styles.name)}>
              {name}
              {leagueNote ? (
                <span {...stylex.props(styles.leagueNote)}>{leagueNote}</span>
              ) : null}
            </h1>
            <p {...stylex.props(styles.pitch)}>{pitch}</p>
            {notice}
            <OnePagerLinks
              name={name}
              site={site}
              links={links}
              renderLink={renderLink}
            />
          </div>
        </div>
        {live}
      </div>
    </header>
  );
}
